/* =========================================================================
   ClueVine — Tangle (anagram unscramble)
   Rearrange the scrambled letters to find the hidden word before the soft
   timer runs out. Forgiving by design: any valid anagram of the letters counts,
   not just one target word.
   ========================================================================= */

import { pickAvoiding } from '../../engine/seed';
import type {
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  MoveFeedback,
  ModeScore,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';

export interface TangleConfig {
  length: number;
  limitMs: number;
}

export interface TangleState {
  answer: string;
  solutions: string[]; // every accepted anagram (includes answer)
  scrambled: string[];
  guess: number[]; // indices into `scrambled`, in placed order
  length: number;
  limitMs: number;
  attempts: number; // wrong submissions
  solved: boolean;
  timedOut: boolean;
  revealedHint: boolean;
}

export type TangleMove =
  | { type: 'place'; index: number }
  | { type: 'unplace' }
  | { type: 'unplaceAt'; slot: number }
  | { type: 'clear' }
  | { type: 'shuffle' }
  | { type: 'submit' }
  | { type: 'timeout' };

export const TANGLE_DIFFICULTIES: DifficultyLevel<TangleConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '4 letters · roomy timer', scoreMultiplier: 0.9, config: { length: 4, limitMs: 90_000 } },
  { id: 'standard', label: 'Garden', blurb: '5 letters', scoreMultiplier: 1, config: { length: 5, limitMs: 75_000 } },
  { id: 'hard', label: 'Thicket', blurb: '6 letters · tight timer', scoreMultiplier: 1.3, config: { length: 6, limitMs: 60_000 } },
];

export function guessWord(state: TangleState): string {
  return state.guess.map((i) => state.scrambled[i]).join('');
}

function rotate<T>(arr: T[], by: number): T[] {
  if (arr.length === 0) return arr;
  const k = ((by % arr.length) + arr.length) % arr.length;
  return [...arr.slice(k), ...arr.slice(0, k)];
}

export function generate(ctx: GenerateContext<TangleConfig>): TangleState {
  const { length, limitMs } = ctx.difficulty.config;
  const pool = ctx.dictionary.answersByLength(length);
  const answer = pickAvoiding(ctx.prng, pool, ctx.avoid);
  const solutions = [...ctx.dictionary.anagramsOf(answer)];

  let scrambled = ctx.prng.shuffle(answer.split(''));
  let tries = 0;
  while (solutions.includes(scrambled.join('')) && tries < 12) {
    scrambled = ctx.prng.shuffle(answer.split(''));
    tries += 1;
  }

  return {
    answer,
    solutions: solutions.length > 0 ? solutions : [answer],
    scrambled,
    guess: [],
    length,
    limitMs,
    attempts: 0,
    solved: false,
    timedOut: false,
    revealedHint: false,
  };
}

export function status(state: TangleState): PuzzleStatus {
  if (state.solved) return 'won';
  if (state.timedOut) return 'lost';
  return 'playing';
}

export function validateMove(state: TangleState, move: TangleMove): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  if (state.guess.length < state.length) return { ok: false, reason: 'Use all the letters' };
  // A full arrangement that isn't a word is accepted as a miss (it costs points).
  return { ok: true };
}

export function reduce(state: TangleState, move: TangleMove): TangleState {
  switch (move.type) {
    case 'place':
      if (
        state.guess.length >= state.length ||
        state.guess.includes(move.index) ||
        move.index < 0 ||
        move.index >= state.scrambled.length
      ) {
        return state;
      }
      return { ...state, guess: [...state.guess, move.index] };
    case 'unplace':
      return state.guess.length ? { ...state, guess: state.guess.slice(0, -1) } : state;
    case 'unplaceAt':
      if (move.slot < 0 || move.slot >= state.guess.length) return state;
      return { ...state, guess: state.guess.filter((_, i) => i !== move.slot) };
    case 'clear':
      return state.guess.length ? { ...state, guess: [] } : state;
    case 'shuffle':
      return { ...state, scrambled: rotate(state.scrambled, 2), guess: [] };
    case 'submit':
      if (state.guess.length < state.length) return state;
      if (state.solutions.includes(guessWord(state))) return { ...state, solved: true };
      return { ...state, attempts: state.attempts + 1, guess: [] };
    case 'timeout':
      return state.solved ? state : { ...state, timedOut: true };
    default:
      return state;
  }
}

export function handleKey(state: TangleState, key: InputKey): TangleMove | null {
  if (key.type === 'char') {
    const index = state.scrambled.findIndex(
      (ch, i) => ch === key.value && !state.guess.includes(i),
    );
    return index >= 0 ? { type: 'place', index } : null;
  }
  if (key.type === 'backspace') {
    return state.guess.length ? { type: 'unplace' } : null;
  }
  return { type: 'submit' };
}

export function moveFeedback(prev: TangleState, next: TangleState): MoveFeedback | null {
  if (next.attempts > prev.attempts) {
    return { message: `Not a word — that’s ${next.attempts} miss${next.attempts === 1 ? '' : 'es'}`, tone: 'error' };
  }
  return null;
}

export function hint(state: TangleState): HintOutcome<TangleState> | null {
  if (status(state) !== 'playing' || state.revealedHint) return null;
  return {
    message: `The word starts with “${state.answer[0].toUpperCase()}”.`,
    penalty: 35,
    state: { ...state, revealedHint: true },
  };
}

export function score(state: TangleState): ModeScore {
  if (status(state) !== 'won') {
    return { base: 0, lines: [{ label: 'Not solved', value: 0, kind: 'base' }], parMs: state.limitMs * 0.5 };
  }
  const base = Math.max(120, state.length * 130 - state.attempts * 40);
  return {
    base,
    lines: [{ label: `Unscrambled (${state.attempts} miss${state.attempts === 1 ? '' : 'es'})`, value: base, kind: 'base' }],
    parMs: state.limitMs * 0.5,
    perfect: state.attempts === 0 && !state.revealedHint,
  };
}

export function summarize(state: TangleState): ResultSummary {
  const won = status(state) === 'won';
  return {
    status: won ? 'won' : 'lost',
    headline: won
      ? state.attempts === 0
        ? 'Unscrambled — flawless!'
        : `Unscrambled after ${state.attempts} miss${state.attempts === 1 ? '' : 'es'}`
      : 'Time’s up',
    answer: state.answer.toUpperCase(),
    shareGrid: [won ? `🧩 solved in ${state.attempts + 1}` : '⌛ time up'],
  };
}

export function deserialize(raw: unknown): TangleState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<TangleState>;
  if (typeof s.answer !== 'string' || !Array.isArray(s.scrambled) || !Array.isArray(s.solutions)) {
    return null;
  }
  if (!Array.isArray(s.guess) || typeof s.length !== 'number') return null;
  return {
    answer: s.answer,
    solutions: s.solutions.filter((w): w is string => typeof w === 'string'),
    scrambled: s.scrambled.filter((c): c is string => typeof c === 'string'),
    guess: s.guess.filter((n): n is number => typeof n === 'number'),
    length: s.length,
    limitMs: typeof s.limitMs === 'number' ? s.limitMs : 75_000,
    attempts: typeof s.attempts === 'number' ? s.attempts : 0,
    solved: Boolean(s.solved),
    timedOut: Boolean(s.timedOut),
    revealedHint: Boolean(s.revealedHint),
  };
}
